// rfmActions.js — Smart Next-Best-Action recommendations per Saudi segment.
// Each segment maps to: priority (P0/P1/P2), action title, body, suggested WhatsApp
// template, and visual colors. Aligned with the new unified Saudi-Pro segmentation.

export const RFM_ACTIONS = {
  gold_vip: {
    priority: "P0",
    icon: "👑",
    title: "كافئهم — اجعلهم يشعرون بالتميّز",
    body: "هؤلاء قلب المقهى. لا تبِع لهم — أعطِهم. وصول مبكر للقائمة الجديدة، مشروب باسمهم، أو دعوة لحدث خاص. اتصل شخصياً عند أي غياب يتجاوز 21 يوم.",
    suggested_template: "thanks",
    color: "#D97706",
    bg: "rgba(217,119,6,0.10)",
  },
  rising_star: {
    priority: "P0",
    icon: "🌟",
    title: "رشّحه للترقية إلى VIP",
    body: "إنفاقه ممتاز رغم قِلّة زياراته. ادفعه لزيارة ثالثة برسالة شخصية + عرض «اكتشف هذا المشروب الجديد قبل الجميع».",
    suggested_template: "offer",
    color: "#7C3AED",
    bg: "rgba(124,58,237,0.08)",
  },
  loyal: {
    priority: "P1",
    icon: "✨",
    title: "نمّ متوسط فاتورته",
    body: "زياراته منتظمة — استثمر في رفع AOV. عروض upgrade (لاتيه ➜ مع شوكولاتة)، أو حزمة «قهوة + كرواسون» بسعر مميّز.",
    suggested_template: "offer",
    color: "#0F766E",
    bg: "rgba(15,118,110,0.08)",
  },
  new: {
    priority: "P0",
    icon: "🆕",
    title: "اصنع التجربة الثانية فوراً",
    body: "أرسل رسالة ترحيب خلال 48 ساعة من الزيارة الأولى + كوبون «القهوة الثانية على حسابنا». الزيارة الثانية ترفع الاحتفاظ 3×.",
    suggested_template: "welcome",
    color: "#3B82F6",
    bg: "rgba(59,130,246,0.08)",
  },
  promising: {
    priority: "P0",
    icon: "🌱",
    title: "نمو سريع — حفّز اليوم",
    body: "زاروا حديثاً جداً (آخر 14 يوم). تواصل شخصي + سؤال عن تجربتهم + عرض ثالث.",
    suggested_template: "welcome",
    color: "#0891B2",
    bg: "rgba(8,145,178,0.08)",
  },
  dormant: {
    priority: "P1",
    icon: "😴",
    title: "أيقظهم قبل أن يندثروا",
    body: "غابوا 46-90 يوم. أرسل عرض إغراء (خصم 30% على المشروب المفضّل) خلال 7 أيام. الفرصة لا تزال مفتوحة.",
    suggested_template: "missed",
    color: "#F59E0B",
    bg: "rgba(245,158,11,0.10)",
  },
  at_risk: {
    priority: "P0",
    icon: "🚨",
    title: "اتصل بهم اليوم — لا تنتظر",
    body: "هؤلاء كانوا VIP وغابوا. كل واحد يساوي عشرات الجدد. اتصال شخصي من المدير + هدية. هذا التصنيف الأعلى قيمة لاستثمار وقتك.",
    suggested_template: "missed",
    color: "#DC2626",
    bg: "rgba(220,38,38,0.12)",
  },
  lost: {
    priority: "P2",
    icon: "💔",
    title: "حملة استرداد جماعية فقط",
    body: "تكلفة استرداد فردية عالية. انتظر حدث كبير (افتتاح فرع، شهر رمضان، عرض موسمي) وأرسل دعوة جماعية مميزة.",
    suggested_template: "missed",
    color: "#991B1B",
    bg: "rgba(153,27,27,0.08)",
  },
  drifter: {
    priority: "P2",
    icon: "🍃",
    title: "رسالة ودية بدون استثمار",
    body: "زياراتهم قليلة وإنفاقهم منخفض. أرسل رسالة موسمية واحدة (عيد/رمضان) — لا تهدر ميزانية تسويق عليهم.",
    suggested_template: "offer",
    color: "#6B7280",
    bg: "rgba(107,114,128,0.06)",
  },
  regular: {
    priority: "P2",
    icon: "👤",
    title: "احتفظ بهم بأقل تكلفة",
    body: "قاعدة منتظمة بإنفاق متوسط. حملة عامة شهرية كافية. ركّز ميزانية الـPersonalization على شرائح أعلى.",
    suggested_template: "offer",
    color: "var(--brown)",
    bg: "rgba(107,78,61,0.06)",
  },
};

export const PRIORITY_META = {
  P0: { label: "🔴 عاجل اليوم",    color: "#B91C1C", bg: "rgba(185,28,28,0.10)",  border: "#B91C1C" },
  P1: { label: "🟠 هذا الأسبوع",   color: "#D97706", bg: "rgba(217,119,6,0.08)",  border: "#D97706" },
  P2: { label: "🟢 مراقبة دورية",   color: "#0F766E", bg: "rgba(15,118,110,0.08)", border: "#0F766E" },
};

/**
 * Returns a sorted list of Quick-Wins: segments with active customers that need urgent action.
 * Ordered by priority then count desc.
 */
export function buildQuickWins(segments, customers, classify) {
  if (!segments || !customers?.length) return [];
  const counts = {};
  for (const c of customers) {
    const k = classify(c) || "regular";
    counts[k] = (counts[k] || 0) + 1;
  }
  const list = [];
  for (const seg of segments) {
    const sid = seg.id;
    if (sid === "all" || !RFM_ACTIONS[sid]) continue;
    const n = counts[sid] || 0;
    if (n === 0) continue;
    const action = RFM_ACTIONS[sid];
    list.push({
      segmentId: sid,
      segmentLabel: seg.label,
      count: n,
      ...action,
    });
  }
  const order = { P0: 0, P1: 1, P2: 2 };
  list.sort((a, b) => (order[a.priority] - order[b.priority]) || (b.count - a.count));
  return list;
}
